import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form } from '@/components/ui/form';
import { useBillingAddressForm } from '@/hooks/useBillingAddressForm';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { BillingAddressFormData } from '@/types';
import { useState, useImperativeHandle, forwardRef } from 'react';
import { BillingAddressFormFields } from './BillingAddressFormFields';

/**
 * Billing Address Form Component
 * Handles billing address collection with conditional fields based on toggle switch
 * Follows React Hook Form patterns with proper validation and error handling
 */
interface BillingAddressFormProps {
  formData?: Partial<BillingAddressFormData>;
  onSubmit?: (data: Partial<BillingAddressFormData>) => void;
  onError?: (error: Error) => void;
  onChange?: (data: Partial<BillingAddressFormData>) => void;
  className?: string;
}

export interface BillingAddressFormRef {
  triggerValidation: () => Promise<boolean>;
}

export const BillingAddressForm = forwardRef<
  BillingAddressFormRef,
  BillingAddressFormProps
>(({ formData, onSubmit, onError, onChange, className }, ref) => {
  const { t: tForms } = useFormsTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { form, handleSubmit, isDifferentFromAccount } = useBillingAddressForm({
    defaultValues: formData,
    onSubmit: async data => {
      try {
        setSubmitError(null);
        if (onSubmit) {
          await onSubmit(data);
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : tForms('billingAddress.errors.submitFailed');
        setSubmitError(errorMessage);
        if (onError) {
          onError(error instanceof Error ? error : new Error(errorMessage));
        }
      }
    },
    onError,
    onChange: onChange ? data => onChange(data) : undefined,
  });

  // Expose form methods via ref
  useImperativeHandle(
    ref,
    () => ({
      triggerValidation: async () => {
        const isValid = await form.trigger();
        return isValid;
      },
    }),
    [form]
  );

  return (
    <div className={className}>
      {submitError && (
        <Alert variant='destructive' className='mb-4'>
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form
          id='account-billing-form'
          onSubmit={handleSubmit}
          className='space-y-6'
        >
          {/* Form Fields */}
          <BillingAddressFormFields
            control={form.control}
            isDifferentFromAccount={isDifferentFromAccount}
            watchedBillingCountry={form.watch('billingCountry')}
          />
        </form>
      </Form>
    </div>
  );
});

export default BillingAddressForm;
